import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    Chip,
    Modal,
    Box,
    CircularProgress,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import TableActionMenu from 'components/TableActionMenu';
import MonoText from 'components/MonoText';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import { useCurrency } from 'context/CurrencyContext';
import NewCouponModal from 'components/NewCouponModal';

const Coupons = () => {
    const [coupons, setCoupons] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        coupon: null,
        loading: false,
    });

    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate, canDelete } = usePermission('coupons');
    const { formatCurrency } = useCurrency();

    useEffect(() => {
        fetchCoupons();
    }, []);

    const fetchCoupons = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/coupons/');
            setCoupons(response.data || []);
        } catch (error) {
            console.error('Error fetching coupons:', error);
            setSnackbar({ open: true, message: 'Error loading coupons', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveCoupon = async (payload) => {
        try {
            await axiosInstance.post('/coupons/', payload);
            setSnackbar({ open: true, message: 'Coupon created successfully', severity: 'success' });
            fetchCoupons();
        } catch (error) {
            console.error('Error saving coupon:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/coupons/${rowData.id}`);
    };

    const handleDeleteClick = (coupon) => {
        setDeleteDialog({
            open: true,
            coupon,
            loading: false,
        });
    };

    const handleDeleteCancel = () => {
        if (deleteDialog.loading) return;
        setDeleteDialog({
            open: false,
            coupon: null,
            loading: false,
        });
    };

    const handleDeleteConfirm = async () => {
        const { coupon } = deleteDialog;
        if (!coupon) return;

        setDeleteDialog(prev => ({ ...prev, loading: true }));
        try {
            await axiosInstance.delete(`/coupons/${coupon.id}`);
            setCoupons(prev => prev.filter(c => c.id !== coupon.id));
            setSnackbar({ open: true, message: 'Coupon deleted successfully', severity: 'success' });
            setDeleteDialog({ open: false, coupon: null, loading: false });
        } catch (error) {
            console.error('Error deleting coupon:', error);
            const detail = error.response?.data?.detail;
            setSnackbar({
                open: true,
                message: typeof detail === 'string' ? detail : 'Error deleting coupon. Please try again.',
                severity: 'error',
            });
            setDeleteDialog(prev => ({ ...prev, loading: false }));
        }
    };

    const columns = useMemo(() => [
        {
            Header: "ID",
            accessor: "id",
            width: "6%",
            Cell: ({ row }) => (
                <MDTypography
                    component="span"
                    onClick={() => handleRowClick(row.original)}
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                >
                    {`#${row.original.id}`}
                </MDTypography>
            ),
        },
        {
            Header: "Coupon Name",
            accessor: "name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{
                        cursor: 'pointer',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: 1.35,
                        display: 'inline-block',
                        maxWidth: '100%',
                    }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value || '-'}
                </MDTypography>
            ),
        },
        {
            Header: "Code",
            accessor: "code",
            width: "11%",
            Cell: ({ value }) => <MonoText>{value || '-'}</MonoText>,
        },
        {
            Header: "Type",
            accessor: "coupon_type",
            width: "9%",
            Cell: ({ value }) => (
                <Chip
                    label={value || "General"}
                    size="small"
                    sx={{
                        bgcolor: value === "Cancellation" ? "#E3F2FD" : "#F0F0F0",
                        color: value === "Cancellation" ? "#1565C8" : "#616161",
                        fontWeight: 600,
                        fontSize: "0.65rem",
                        height: 20,
                    }}
                />
            ),
        },
        {
            Header: "Package",
            accessor: (row) => row.trip_package?.title || (row.trip_package_id ? `Package #${row.trip_package_id}` : ''),
            id: "package",
            width: "13%",
            Cell: ({ row }) => {
                const packageTitle = row.original.trip_package?.title || (row.original.trip_package_id ? `Package #${row.original.trip_package_id}` : null);
                if (!packageTitle) return <MDTypography variant="caption">-</MDTypography>;
                return (
                    <MDTypography
                        component="span"
                        variant="caption"
                        color="info"
                        fontWeight="medium"
                        sx={{
                            cursor: 'pointer',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            lineHeight: 1.35,
                            display: 'inline-block',
                            maxWidth: '100%',
                        }}
                        onClick={() => row.original.trip_package_id && navigate(`/trip-packages/${row.original.trip_package_id}`)}
                    >
                        {packageTitle}
                    </MDTypography>
                );
            },
        },
        {
            Header: "Customer",
            accessor: (row) => row.customer?.name || (row.customer_id ? `#${row.customer_id}` : ''),
            id: "customer",
            width: "12%",
            Cell: ({ row }) => {
                const custName = row.original.customer?.name || (row.original.customer_id ? `#${row.original.customer_id}` : null);
                if (!custName) return <MDTypography variant="caption">-</MDTypography>;
                return (
                    <MDTypography
                        component="span"
                        variant="caption"
                        color="info"
                        fontWeight="medium"
                        sx={{
                            cursor: 'pointer',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            lineHeight: 1.35,
                            display: 'inline-block',
                            maxWidth: '100%',
                        }}
                        onClick={() => row.original.customer_id && navigate(`/accounts/${row.original.customer_id}`)}
                    >
                        {custName}
                    </MDTypography>
                );
            },
        },
        {
            Header: "Value",
            accessor: "value",
            width: "10%",
            align: "right",
            Cell: ({ value, row }) => {
                if (value == null || value === '') return <MonoText align="right">-</MonoText>;
                if (row.original.discount_type === 'Percent') {
                    return <MonoText align="right">{`${value}%`}</MonoText>;
                }
                return (
                    <MonoText align="right">
                        {formatCurrency(value, row.original.currency)}
                    </MonoText>
                );
            },
        },
        {
            Header: "Usage",
            accessor: "used_count",
            width: "6%",
            Cell: ({ row }) => (
                <MonoText>
                    {`${row.original.used_count || 0} / ${row.original.available_count || 0}`}
                </MonoText>
            ),
        },
        {
            Header: "Status",
            accessor: (row) => {
                const expDate = row.expiration_date ? new Date(row.expiration_date) : null;
                const now = new Date();
                const isExpired = expDate && expDate < now;
                const isUsedUp = (row.available_count || 0) > 0 && (row.used_count || 0) >= (row.available_count || 0);
                return isExpired ? 'Expired' : (isUsedUp ? 'Used Up' : 'Active');
            },
            id: "status",
            width: "5%",
            Cell: ({ row }) => {
                const expDate = row.original.expiration_date ? new Date(row.original.expiration_date) : null;
                const now = new Date();
                const isExpired = expDate && expDate < now;
                const isUsedUp = (row.original.available_count || 0) > 0 && (row.original.used_count || 0) >= (row.original.available_count || 0);
                let label = 'Active';
                let color = 'success';
                if (isExpired) {
                    label = 'Expired';
                    color = 'error';
                } else if (isUsedUp) {
                    label = 'Used Up';
                    color = 'default';
                }
                return (
                    <Chip
                        label={label}
                        size="small"
                        color={color}
                        sx={{ fontWeight: 600, fontSize: "0.65rem", height: 20 }}
                    />
                );
            },
        },
        {
            Header: "Actions",
            accessor: "actions",
            width: "5%",
            align: "center",
            disableSortBy: true,
            Cell: ({ row }) => (
                canDelete ? (
                    <TableActionMenu
                        row={row.original}
                        onView={handleRowClick}
                        onDelete={() => handleDeleteClick(row.original)}
                    />
                ) : (
                    <TableActionMenu
                        row={row.original}
                        onView={handleRowClick}
                    />
                )
            ),
        },
    ], [canDelete, formatCurrency]);

    const tableData = useMemo(() => ({
        columns,
        rows: coupons,
    }), [columns, coupons]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Coupons</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Coupon
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : coupons.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Coupon" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <NewCouponModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveCoupon}
            />

            {/* Delete Confirmation Modal */}
            <Modal
                open={deleteDialog.open}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-coupon-modal"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '90%', sm: 450 },
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                    }}
                >
                    <MDTypography variant="h6" component="h2" mb={2}>
                        Delete Coupon
                    </MDTypography>
                    <MDTypography variant="body2" color="text" mb={3}>
                        Are you sure you want to delete coupon <strong>{deleteDialog.coupon?.name}</strong> ({deleteDialog.coupon?.code})? This action cannot be undone.
                    </MDTypography>
                    <MDBox display="flex" justifyContent="flex-end" gap={2}>
                        <MDButton
                            onClick={handleDeleteCancel}
                            variant="outlined"
                            color="secondary"
                            disabled={deleteDialog.loading}
                        >
                            Cancel
                        </MDButton>
                        <MDButton
                            onClick={handleDeleteConfirm}
                            variant="gradient"
                            color="error"
                            disabled={deleteDialog.loading}
                        >
                            {deleteDialog.loading ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                'Delete'
                            )}
                        </MDButton>
                    </MDBox>
                </Box>
            </Modal>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default Coupons;
